import { FC, useState } from "react";

import {
  Button,
  Flyout,
  FlyoutHeader,
  FlyoutTitle,
} from "@monolith-forensics/monolith-ui";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  EditIcon,
  TrashIcon,
} from "lucide-react";

import {
  DetailItem,
  DetailLabel,
  DetailValue,
  FlyoutContainer,
  HeaderContent,
} from "@/components/Flyouts/components/MonolithFlyoutComponents";
import { usePermissions } from "@/hooks/usePermissions";
import { DeleteValidationModal } from "@/Features/LabAssets/Modals/DeleteValidationModal";
import { EditValidationModal } from "@/Features/LabAssets/Modals/EditValidationModal";
import { getValidationLabel } from "@/Features/LabAssets/Forms/validationForm.utils";
import { useAssetValidationItem } from "../asset-validations.query";
import { ValidationReports } from "./ValidationReports";
import {
  AssetValidationColumnDefinitions,
  AssetValidationRow,
  UsersById,
  withUserNames,
} from "../Tables/AssetValidationColumnDefinitions";

interface AssetValidationFlyoutProps {
  show: boolean;
  onClose?: () => void;
  defaultInfo: AssetValidationRow;
  // The table's users map, so a refetched row (ids only) still shows names.
  usersById: UsersById;
  onNext?: () => void;
  onPrevious?: () => void;
}

/**
 * Quick-view of one validation opened from AssetValidationsTable (ENG-1000).
 * Mirrors LabAssetFlyout: seed from the table row, refetch through the item
 * hook, Edit/Delete through modals that maintain the caches themselves.
 * Reports (ENG-1673) list below the description for users who can read
 * asset attachments.
 */
export const AssetValidationFlyout: FC<AssetValidationFlyoutProps> = ({
  show,
  onClose,
  defaultInfo,
  usersById,
  onNext,
  onPrevious,
}) => {
  const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { data } = useAssetValidationItem(defaultInfo.uuid, {
    initialData: defaultInfo,
    initialDataUpdatedAt: 0,
  });

  // null is the hook's not-found signal: deleted since the table row loaded.
  const validationGone = data === null;

  // The item cache holds bare rows (GET and PATCH return ids only); re-apply
  // the names from the table's map so the cells never blank after a refetch.
  const itemData: AssetValidationRow = data?.uuid
    ? withUserNames([data], usersById)[0]
    : defaultInfo;

  return (
    <Flyout open={show} onClose={() => onClose?.()}>
      <FlyoutHeader>
        <FlyoutTitle>{getValidationLabel(itemData)}</FlyoutTitle>
        <HeaderContent>
          <div>Validation</div>
          {!validationGone &&
            hasPermission(MONOLITH_PERMISSIONS.VALIDATION_UPDATE) && (
              <Button
                size="xxs"
                variant="text"
                leftSection={<EditIcon size={14} />}
                color="primary"
                onClick={() => setShowEditModal(true)}
              >
                Edit
              </Button>
            )}
          {!validationGone &&
            hasPermission(MONOLITH_PERMISSIONS.VALIDATION_DELETE) && (
              <Button
                size="xxs"
                variant="text"
                leftSection={<TrashIcon size={14} />}
                color="error"
                onClick={() => setShowDeleteModal(true)}
              >
                Delete
              </Button>
            )}
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: 10,
              alignItems: "center",
              marginLeft: "auto",
            }}
          >
            <Button
              size="xxs"
              variant="outlined"
              onClick={() => onPrevious?.()}
              style={{ padding: "2px 4px" }}
            >
              <ChevronLeftIcon size={14} />
            </Button>
            <Button
              size="xxs"
              variant="outlined"
              onClick={() => onNext?.()}
              style={{ padding: "2px 4px" }}
            >
              <ChevronRightIcon size={14} />
            </Button>
          </div>
        </HeaderContent>
      </FlyoutHeader>
      {validationGone ? (
        <FlyoutContainer>
          <DetailValue>
            There was an error loading this validation. It may have been deleted
            since this table initially loaded.
          </DetailValue>
        </FlyoutContainer>
      ) : (
        <FlyoutContainer>
          <div className="event-details">
            {/* Every column, hidden ones included: the table's `render` owns
                the date/user formatting. Description last, full width. */}
            {AssetValidationColumnDefinitions.filter(
              (col) => col.dataField !== "description"
            ).map((col) => (
              <DetailItem key={col.dataField}>
                <DetailLabel>{col.caption}</DetailLabel>
                <DetailValue>
                  {col.render
                    ? col.render({ rowData: itemData })
                    : (itemData[col.dataField] as string | number | null)}
                </DetailValue>
              </DetailItem>
            ))}
            <DetailItem>
              <DetailLabel>Description</DetailLabel>
            </DetailItem>
            <DetailValue>{itemData.description || "None"}</DetailValue>
          </div>
          {hasPermission(MONOLITH_PERMISSIONS.ASSET_ATTACHMENT_READ_ALL) && (
            // Keyed so Next/Previous drops the open viewer with the old list.
            <ValidationReports
              key={itemData.uuid}
              assetUuid={itemData.asset_uuid}
              validationUuid={itemData.uuid}
            />
          )}
        </FlyoutContainer>
      )}
      {showEditModal && (
        <EditValidationModal
          show={showEditModal}
          onClose={() => setShowEditModal(false)}
          defaultInfo={itemData}
          completedByName={itemData.completed_by_name}
        />
      )}
      {showDeleteModal && (
        <DeleteValidationModal
          show={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          // The row is gone; the list cache was invalidated by the modal.
          onDelete={() => onClose?.()}
          defaultInfo={itemData}
        />
      )}
    </Flyout>
  );
};
